/** @forge-header
 * @id          kalshiApiMarkets
 * @role        Market discovery endpoints | series, events, paginated market fetch
 * @pattern     adapter
 * @purity      api
 * @units       n/a
 * @upstream    kalshiApi/client:authenticatedFetch | types:KalshiSeries,KalshiMarket,KalshiEvent
 * @downstream  kalshiApi/index (via createKalshiClient)
 * @exports     getSeries:fn | getEvents:fn | getMarkets:fn | getMarket:fn | getEvent:fn
 * @test        untested (integration-level)
 */

import type { KalshiSeries, KalshiMarket, KalshiEvent } from '../../types';
import { authenticatedFetch } from './client';

/**
 * Get series (categories)
 */
export async function getSeries(
  params: { category?: string; include_volume?: boolean } = {}
): Promise<KalshiSeries[]> {
  const query: Record<string, string> = {};
  if (params.category) {
    query.category = params.category;
  }
  // Always include volume for richer display
  query.include_volume = params.include_volume !== false ? 'true' : 'false';

  const response = await authenticatedFetch('GET', '/trade-api/v2/series', query);
  const data = await response.json();

  // Handle different response formats and normalize ticker field
  const normalize = (series: KalshiSeries[]): KalshiSeries[] =>
    series.map((s) => ({
      ...s,
      series_ticker: s.series_ticker || s.ticker || '',
    }));

  if (Array.isArray(data)) {
    return normalize(data);
  }
  if (data.series && Array.isArray(data.series)) {
    return normalize(data.series);
  }
  if (data.results && Array.isArray(data.results)) {
    return normalize(data.results);
  }

  return [];
}

/**
 * Get events (for a series or filtered by status)
 */
export async function getEvents(
  params: {
    series_ticker?: string;
    status?: 'open' | 'closed' | 'settled';
    with_nested_markets?: boolean;
    limit?: number;
  } = {}
): Promise<KalshiEvent[]> {
  const query: Record<string, string> = {};
  if (params.series_ticker) query.series_ticker = params.series_ticker;
  if (params.status) query.status = params.status;
  if (params.with_nested_markets) query.with_nested_markets = 'true';
  if (params.limit) query.limit = params.limit.toString();

  const response = await authenticatedFetch('GET', '/trade-api/v2/events', query);
  const data = await response.json();

  // Handle different response formats
  if (Array.isArray(data)) {
    return data;
  }
  if (data.events && Array.isArray(data.events)) {
    return data.events;
  }
  if (data.results && Array.isArray(data.results)) {
    return data.results;
  }

  return [];
}

/**
 * Get markets
 */
export async function getMarkets(
  params: {
    series_ticker?: string;
    event_ticker?: string;
    status?: string;
    limit?: number;
  } = {}
): Promise<KalshiMarket[]> {
  // Normalize ticker field - API returns 'ticker' but we use 'market_ticker'
  // Also map `expiration_time` → `expiry_time` (API returns `expiration_time`)
  const normalize = (markets: KalshiMarket[]): KalshiMarket[] =>
    markets.map((m) => ({
      ...m,
      market_ticker: m.market_ticker || (m as unknown as { ticker?: string }).ticker || '',
      expiry_time:
        m.expiry_time || (m as unknown as { expiration_time?: string }).expiration_time || '',
    }));

  const pageLimit = params.limit ?? 200;
  const maxPages = 5; // Cap to avoid runaway pagination
  const pageDelayMs = 500; // Delay between pages to respect rate limits

  const allMarkets: KalshiMarket[] = [];
  let cursor: string | undefined;

  for (let page = 0; page < maxPages; page++) {
    const query: Record<string, string> = {};
    if (params.series_ticker) query.series_ticker = params.series_ticker;
    if (params.event_ticker) query.event_ticker = params.event_ticker;
    if (params.status) query.status = params.status;
    query.limit = pageLimit.toString();
    if (cursor) query.cursor = cursor;

    const response = await authenticatedFetch('GET', '/trade-api/v2/markets', query);
    const data = await response.json();

    // Extract markets from response
    let pageMarkets: KalshiMarket[] = [];
    if (Array.isArray(data)) {
      pageMarkets = data;
    } else if (data.markets && Array.isArray(data.markets)) {
      pageMarkets = data.markets;
    } else if (data.results && Array.isArray(data.results)) {
      pageMarkets = data.results;
    }

    allMarkets.push(...pageMarkets);

    // Check for next page cursor
    const nextCursor = data.cursor as string | undefined;
    if (!nextCursor || pageMarkets.length < pageLimit) {
      break; // No more pages
    }

    cursor = nextCursor;

    // Delay between pages to avoid rate limiting
    if (page < maxPages - 1) {
      await new Promise((resolve) => setTimeout(resolve, pageDelayMs));
    }
  }

  return normalize(allMarkets);
}

/**
 * Get a single market by ticker (to access the market title).
 */
export async function getMarket(marketTicker: string): Promise<KalshiMarket | null> {
  try {
    const response = await authenticatedFetch(
      'GET',
      `/trade-api/v2/markets/${encodeURIComponent(marketTicker)}`
    );
    const data = await response.json();
    // Kalshi returns { market: { ... } } for this endpoint per docs.
    const rec = data as unknown as Record<string, unknown>;
    const market =
      (rec && typeof rec === 'object' && 'market' in rec
        ? (rec as { market?: unknown }).market
        : null) ?? data;
    return market as KalshiMarket;
  } catch (error) {
    console.warn(`Failed to fetch market ${marketTicker}:`, error);
    return null;
  }
}

/**
 * Get event details
 */
export async function getEvent(eventTicker: string): Promise<KalshiEvent | null> {
  try {
    const response = await authenticatedFetch('GET', `/trade-api/v2/events/${eventTicker}`);
    const data = await response.json();
    // API returns { event: {...}, markets: [...] } wrapper
    if (data && typeof data === 'object' && 'event' in data) {
      const event = data.event as KalshiEvent;
      // If event doesn't have start_time but markets do have close_time, use that
      // For NBA games, close_time is when betting closes (game start)
      if (
        !event.start_time &&
        data.markets &&
        Array.isArray(data.markets) &&
        data.markets.length > 0
      ) {
        const firstMarket = data.markets[0];
        if (firstMarket.close_time) {
          event.start_time = firstMarket.close_time;
        }
      }
      return event;
    }
    return data as KalshiEvent;
  } catch (error) {
    console.warn(`Failed to fetch event ${eventTicker}:`, error);
    return null;
  }
}
